import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";

export default function LtvMatrixTab() {
  const [rows, setRows] = useState(null);
  const [version, setVersion] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/policy/ltv-matrix").then(r => { setRows(r.data.rows); setVersion(r.data.version); }).catch(() => toast.error("Could not load matrix"));
  }, []);

  if (!rows) return <div className="text-sm text-slate-500">Loading matrix…</div>;

  const setCell = (i, key, value) => setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const numeric = rows.map(r => ({
        ...r,
        max_ltv: Number(r.max_ltv), min_down_pct: Number(r.min_down_pct),
        roi_min: Number(r.roi_min), roi_max: Number(r.roi_max), max_tenure: Number(r.max_tenure),
      }));
      const { data } = await api.put("/policy/ltv-matrix", { rows: numeric, reason });
      setRows(data.rows); setVersion(data.version); setReason("");
      toast.success(`LTV/ROI matrix saved — v${data.version}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ltv-matrix-tab">
      <Card className="border-slate-200 overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
          <div className="text-sm font-semibold text-slate-900">Risk band × CarDr grade matrix <span className="text-xs font-normal text-slate-500">v{version}</span></div>
        </div>
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Band</th><th className="text-left px-3 py-2">Grade</th>
              <th className="text-left px-3 py-2">Max LTV</th><th className="text-left px-3 py-2">Min down %</th>
              <th className="text-left px-3 py-2">ROI min</th><th className="text-left px-3 py-2">ROI max</th>
              <th className="text-left px-3 py-2">Max tenure</th><th className="text-center px-3 py-2">GPS</th>
              <th className="text-center px-3 py-2">Guarantor</th><th className="text-center px-3 py-2">Co-borrower</th>
              <th className="text-center px-3 py-2">Manual</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.band}-${r.grade}`} className="border-t border-slate-100" data-testid={`matrix-row-${r.band}-${r.grade}`}>
                <td className="px-3 py-1.5 font-semibold">{r.band}</td>
                <td className="px-3 py-1.5">{r.grade}</td>
                <td className="px-3 py-1.5"><Input className="w-20 h-8" type="number" step={0.01} value={r.max_ltv} onChange={e => setCell(i, "max_ltv", e.target.value)} /></td>
                <td className="px-3 py-1.5"><Input className="w-20 h-8" type="number" step={0.01} value={r.min_down_pct} onChange={e => setCell(i, "min_down_pct", e.target.value)} /></td>
                <td className="px-3 py-1.5"><Input className="w-20 h-8" type="number" step={0.1} value={r.roi_min} onChange={e => setCell(i, "roi_min", e.target.value)} /></td>
                <td className="px-3 py-1.5"><Input className="w-20 h-8" type="number" step={0.1} value={r.roi_max} onChange={e => setCell(i, "roi_max", e.target.value)} /></td>
                <td className="px-3 py-1.5"><Input className="w-20 h-8" type="number" value={r.max_tenure} onChange={e => setCell(i, "max_tenure", e.target.value)} /></td>
                <td className="px-3 py-1.5 text-center"><Checkbox checked={!!r.gps_required} onCheckedChange={v => setCell(i, "gps_required", !!v)} /></td>
                <td className="px-3 py-1.5 text-center"><Checkbox checked={!!r.guarantor_required} onCheckedChange={v => setCell(i, "guarantor_required", !!v)} /></td>
                <td className="px-3 py-1.5 text-center"><Checkbox checked={!!r.coborrower_required} onCheckedChange={v => setCell(i, "coborrower_required", !!v)} /></td>
                <td className="px-3 py-1.5 text-center"><Checkbox checked={!!r.manual_approval} onCheckedChange={v => setCell(i, "manual_approval", !!v)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card className="p-5 border-teal-200 bg-teal-50/50">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-64">
            <Label className="text-xs">Reason for change</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} className="mt-1 bg-white" data-testid="matrix-reason" />
          </div>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="matrix-save-btn">Save matrix</Button>
        </div>
      </Card>
    </div>
  );
}
